import { MutableRefObject } from 'react';
import { Node, CommandProps } from '@tiptap/core';

import getAutoIndexLiteratureCitationsPlugin, {
  type TCitationSeed,
} from './plugins/getAutoIndexLiteratureCitationsPlugin';
import { LITERATURE_CITATION_NODE_NAME } from './constants';
import getLiteratureCitationDropPlugin from './plugins/getLiteratureCitationDropPlugin';
import { markSystemTr } from '../../plugins/shared';
import { TMap } from '@shared/types';
import { EAssessmentDecision } from '@modules/literatures/types';
import { TResolvedLiteratureCitation } from '../../../types/reference.types';
import { formatCitationLabel } from '../../../doc-engine/text';

// Chip visual is binary: either a citation is valid, or it's not. The bubble menu surfaces WHY.
const INVALID_CLASS = 'literature-citation-invalid';

export type TLiteratureCitationState =
  | 'missing'
  | 'archived'
  | 'excluded'
  | 'pending'
  | 'unassessed'
  | 'not-required'
  | 'included';

// WARNING Mirrored in FE/BE - keep in sync. Counterpart: backend/src/documents/src/htmlToPdf/addLiteratureCitationsToHtml.ts
export const classifyLiteratureCitation = (data: TResolvedLiteratureCitation | undefined): TLiteratureCitationState => {
  if (!data) return 'missing';
  if (data.archived) return 'archived';
  if (data.decision === EAssessmentDecision.EXCLUDED) return 'excluded';
  if (data.decision === EAssessmentDecision.PENDING) return 'pending';
  if (data.decision === EAssessmentDecision.NOT_REQUIRED) return 'not-required';
  if (data.decision === null) return 'unassessed';
  return 'included';
};

const resolveStatusClass = (data: TResolvedLiteratureCitation | undefined): string | null => {
  const state = classifyLiteratureCitation(data);
  return state === 'included' || state === 'not-required' ? null : INVALID_CLASS;
};

const applyStatusClass = (dom: HTMLElement, nextClass: string | null) => {
  dom.classList.remove(INVALID_CLASS);
  if (nextClass) dom.classList.add(nextClass);
};

type TLiteratureCitationNodeOptions = {
  /**
   * Live pointer to `doc.resolvedLiteratureCitations`, wired from useOptions.hook.tsx.
   * Exposed through a function because TipTap deep-clones object-valued extension
   * options, which would detach a MutableRefObject from its React-owned source.
   * Read inside `update()` on every tick - the bump command below forces the
   * read to happen when external data changes.
   */
  getStatusDataRef: () => MutableRefObject<TMap<string, TResolvedLiteratureCitation>>;
  /**
   * Citations outside the part the editor holds, when a large document is loaded in parts. Both
   * sides matter: a repeat reuses the number of its first occurrence, which may sit below the part.
   */
  numberingSeed?: () => TCitationSeed | null;
};

declare module '@tiptap/core' {
  interface Commands<ReturnType> {
    literatureCitation: {
      insertLiteratureCitation: (literatureId: string, statusEntry?: TResolvedLiteratureCitation) => ReturnType;
      /**
       * Forces every literature-citation node's NodeView `update()` to fire by
       * toggling its volatile `statusNonce` attr via setNodeMarkup. Mirrors
       * CaptionNode's `bumpCaptionLabelNonce`. Dispatched from a React effect
       * when `doc.resolvedLiteratureCitations` changes - without it, vanilla
       * NodeViews never re-read external data.
       */
      bumpLiteratureCitationStatusNonce: () => ReturnType;
    };
  }
}

// No insert-time validation - the insert dialog pre-filters to accessible, included, version-complete records.
// statusNonce + bumpLiteratureCitationStatusNonce exist solely to force update() when external status data changes.
const LiteratureCitationNode = Node.create<TLiteratureCitationNodeOptions>({
  name: LITERATURE_CITATION_NODE_NAME,

  inline: true,
  group: 'inline',
  atom: true,
  draggable: true,
  selectable: true,

  addOptions() {
    const statusDataRef = { current: {} } as MutableRefObject<TMap<string, TResolvedLiteratureCitation>>;
    return {
      getStatusDataRef: () => statusDataRef,
    };
  },

  addAttributes() {
    return {
      literatureId: {
        default: null,
        parseHTML: (el) => el.getAttribute('data-literature-id'),
        renderHTML: (attributes) => (attributes.literatureId ? { 'data-literature-id': attributes.literatureId } : {}),
      },
      // Persisted to data-index so saved HTML preserves displayed order across reloads; not derivable from DOM position.
      index: {
        default: null,
        parseHTML: (el) => {
          const v = el.getAttribute('data-index');
          const n = v ? Number(v) : NaN;
          return Number.isFinite(n) ? n : null;
        },
        renderHTML: (attributes) =>
          typeof attributes.index === 'number' ? { 'data-index': String(attributes.index) } : {},
      },
      // Volatile: flipped by bumpLiteratureCitationStatusNonce to force update() when external status data changes. Never serialized.
      statusNonce: {
        default: 0,
        rendered: false,
        parseHTML: () => 0,
      },
    };
  },

  renderHTML({ HTMLAttributes }) {
    return ['literature-citation', HTMLAttributes];
  },

  parseHTML() {
    // Require data-literature-id so orphan chips pasted from outside the app don't render as permanent [?].
    return [{ tag: 'literature-citation[data-literature-id]' }];
  },

  // Vanilla NodeView - index is mutated by the auto-index plugin; status class is re-read on every update() via statusDataRef.
  addNodeView() {
    const getStatusDataRef = this.options.getStatusDataRef;

    return ({ node }) => {
      const dom = document.createElement('span');
      dom.className = 'literature-citation';
      dom.setAttribute('data-drag-handle', '');
      dom.textContent = formatCitationLabel(node.attrs.index);
      applyStatusClass(dom, resolveStatusClass(getStatusDataRef().current[node.attrs.literatureId as string]));

      return {
        dom,
        update: (updatedNode) => {
          if (updatedNode.type.name !== node.type.name) return false;
          const next = formatCitationLabel(updatedNode.attrs.index);
          if (dom.textContent !== next) dom.textContent = next;
          applyStatusClass(
            dom,
            resolveStatusClass(getStatusDataRef().current[updatedNode.attrs.literatureId as string])
          );
          return true;
        },
      };
    };
  },

  addProseMirrorPlugins() {
    return [
      getAutoIndexLiteratureCitationsPlugin(this.options.numberingSeed),
      getLiteratureCitationDropPlugin(this.editor),
    ];
  },

  addCommands() {
    return {
      insertLiteratureCitation:
        (literatureId: string, statusEntry?: TResolvedLiteratureCitation) =>
        ({ commands }) => {
          // Write before node creation so the NodeView constructor reads correct status immediately.
          if (statusEntry) {
            const statusDataRef = this.options.getStatusDataRef();
            statusDataRef.current = { ...statusDataRef.current, [literatureId]: statusEntry };
          }
          return commands.insertContent({ type: this.name, attrs: { literatureId } });
        },
      bumpLiteratureCitationStatusNonce:
        () =>
        ({ state, dispatch }: CommandProps) => {
          if (!dispatch) return true;
          let tr = state.tr;
          state.doc.descendants((node, pos) => {
            if (node.type.name === LITERATURE_CITATION_NODE_NAME) {
              const nonce = typeof node.attrs.statusNonce === 'number' ? node.attrs.statusNonce : 0;
              tr = tr.setNodeMarkup(pos, undefined, {
                ...node.attrs,
                statusNonce: (nonce ^ 1) >>> 0, // flip 0/1
              });
            }
            return true;
          });
          dispatch(markSystemTr(tr));
          return true;
        },
    };
  },
});

export default LiteratureCitationNode;
